import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';

export class ManagedSubscription {
  private subscription?: Meteor.SubscriptionHandle;
  // previous subscription is needed because "meteor.susbcribe"
  // is a mix of async and syncrhonous, meaning it gets set delayed
  // which causes it to be set twice when force: true is used
  // and just unsubscribed once because that happens
  // before they run in parallel
  private previousSubscription?: Meteor.SubscriptionHandle;
  public isSubscribed = false;
  private readySubject$ = new BehaviorSubject<boolean>(false);

  subscriptionStarted$ = new Subject<void>();

  // this structure ensures, that when a new subscription is started,
  // the replay of the previous "ready" is no longer shared
  public ready$ = this.subscriptionStarted$.pipe(
    startWith(null),
    switchMap(() =>
      this.readySubject$.pipe(
        // must be distinct becase onStop emits it again which would cause it to re-subscribe on unsubscribe
        distinctUntilChanged(),
        tap(() => {
          // subscribing ready also triggers the subscription if it is not already subscribed
          if (!this.isSubscribed) {
            this.subscribe();
          }
        }),
        filter((ready) => ready),
        shareReplay(1),
      ),
    ),
    // this debounce makes trackers react to the changes, for example the profileUserTracker in profile-edit
    // removing this fails customer-users.spec.ts "should be able to invite another user" for example
    debounceTime(0),
  );

  loading$ = this.ready$.pipe(
    startWith(false),
    map((ready) => !ready),
    shareReplay(1),
  );

  constructor(private subscriptionName: string, private options?: any) {}

  subscribe(force: boolean = false, options?: any): void {
    if (options !== undefined) {
      this.options = options;
    }
    if (force) {
      this.unsubscribe();
    }
    if (this.isSubscribed) {
      return;
    }
    // we need isSubscribed instead of relying on "subscription"
    // because subscription is not set instantly/synchronously by meteor
    this.isSubscribed = true;
    this.subscriptionStarted$.next();
    this.readySubject$.next(false);
    Meteor.setTimeout(() => {
      if (!this.isSubscribed) {
        return;
      }
      this.subscription = Meteor.subscribe(
        this.subscriptionName,
        this.options,
        {
          onReady: () => {
            this.readySubject$.next(true);
          },
          onStop: () => {
            // when there are no results, onStop is called, but the subscription is ready
            this.readySubject$.next(true);
          },
        },
      );
      if (this.previousSubscription) {
        // this log shows that previous subscription is sometimes not stopped
        // when this method is called twice with force: true, because "this.subscription" is not
        // really synchronous (meaning it gets set twice)
        // console.log('stopping a previous subscription');
        this.previousSubscription.stop();
      }
      this.previousSubscription = this.subscription;
    }, 0);
  }

  unsubscribe(): void {
    if (this.isSubscribed) {
      this.isSubscribed = false;
      this.subscription?.stop();
      this.subscription = undefined;
    }
  }
}
